import IconButton from '@components/ui/icon-button';
import { BackHeaderIOsOutlined } from '@icon';
import classNames from 'classnames';
import hoistNonReactStatics from 'hoist-non-react-statics';
import RCDrawer, { DrawerProps as BaseProps } from 'rc-drawer';
import 'rc-drawer/assets/index.css';
import React from 'react';
import { useTheme } from 'styled-components';
import motionProps from '../drawers.motion';
import { DrawerStyles } from '../drawers.styled';
import { useDrawer } from '../drawers.utils';
import {
  DrawerContent,
  DrawerExpandedHeader,
  DrawerHeader,
  clsPrefix,
  drawerScrollHeight,
  getContentSize,
  headerHeight,
  modalClasses,
  topOffset,
  virtualStyle,
} from './life-id-drawer.styled';
import LifeIdScroller from './components/life-id-scroller';

interface DrawerProps extends Omit<BaseProps, 'open' | 'mask' | 'maskClosable'> {
  /**
   * Modal visible status
   */
  visible?: boolean;
  /**
   * Title of modal
   */
  title?: React.ReactNode;
  /**
   * Icon to show on left by title
   */
  Icon?: (p: React.SVGProps<SVGSVGElement>) => JSX.Element;
  /**
   * Extra footer separated from body to be pinned on button of drawer
   */
  footer?: React.ReactNode;
  /**
   * Extra content in header. Use primarily with small round icon buttons
   */
  extra?: React.ReactNode;
  contentContainerStyle?: React.CSSProperties;
  isInfiniteScroll?: boolean;
}

const LifeIdDrawer = (
  {
    children,
    visible,
    // 412 + 1px border
    width = 413,
    title,
    className,
    onClose,
    footer,
    Icon,
    contentContainerStyle,
    isInfiniteScroll,
    extra,
    ...rest
  }: DrawerProps,
  ref?: React.Ref<HTMLDivElement>,
) => {
  const theme = useTheme();

  return (
    <>
      <DrawerStyles />
      <RCDrawer
        open={visible}
        width={width}
        {...rest}
        {...motionProps}
        rootClassName={classNames(clsPrefix, className)}
        mask={false}
        onClose={onClose}
        maskClosable={false}
      >
        <DrawerContent
          className={modalClasses.contentWrapper}
          style={{
            ...contentContainerStyle,
            ...(isInfiniteScroll ? virtualStyle : {}),
          }}
          ref={ref}
        >
          <DrawerHeader>
            <IconButton
              className={modalClasses.clsButton}
              Icon={BackHeaderIOsOutlined}
              size="small"
              shape="round"
              variant="ghost-secondary"
              surface="on-dark"
              onClick={onClose}
            />
            {Icon ? (
              <Icon
                className={modalClasses.icon}
                width={24}
                height={24}
                color={theme.color.tint.tertiaryDark}
              />
            ) : null}
            <div className={modalClasses.title}>{title}</div>
            {extra ? <div className={modalClasses.extra}>{extra}</div> : null}
          </DrawerHeader>
          <div className={classNames(modalClasses.body, 'drawer-body')}>{children}</div>
          {footer ? <div className={modalClasses.footer}>{footer}</div> : null}
        </DrawerContent>
      </RCDrawer>
    </>
  );
};

LifeIdDrawer.Header = DrawerExpandedHeader;
LifeIdDrawer.Scroller = LifeIdScroller;
LifeIdDrawer.useDrawer = useDrawer;

LifeIdDrawer.headerHeight = headerHeight;
LifeIdDrawer.topOffset = topOffset;
LifeIdDrawer.getContentSize = getContentSize;
LifeIdDrawer.drawerScrollHeight = drawerScrollHeight;

export default hoistNonReactStatics(React.forwardRef(LifeIdDrawer), LifeIdDrawer);
